<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import NewHeader from '~/components/NewHeader.vue';
import NewFooter from '~/components/NewFooter.vue';
import api from '~/api';
import axios from 'axios';

const authStore = useAuthStore();
const commStore = useCommStore();
const router = useRouter();
const localePath = useLocalePath();

useHead({
  title: 'Image to Video - AI Video Generator | BestFaceSwap',
  meta: [
    { name: 'description', content: 'Transform your images into stunning AI-generated videos with 6 different AI models. Support for multiple resolutions, durations, and aspect ratios.' },
  ],
});

// ==================== Model Configs ====================
interface ModelConfig {
  id: string;
  label: string;
  resolutions: string[];
  durations: string[];
  maxPromptLength: number;
  needsAspectRatio: boolean;
  aspectRatios?: string[];
  supportsAudio: boolean;
  defaultAudio: boolean;
}

const MODEL_CONFIGS: ModelConfig[] = [
  {
    id: 'i2v_aipose_video_4',
    label: 'BestFaceSwap 4.0',
    resolutions: ['HD', 'FHD'],
    durations: ['5', '10'],
    maxPromptLength: 2000,
    needsAspectRatio: true,
    aspectRatios: ['16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'],
    supportsAudio: false,
    defaultAudio: false,
  },
  {
    id: 'i2v_seedance_1_5_pro',
    label: 'Seedance 1.5 Pro',
    resolutions: ['480p', '720p', '1080p'],
    durations: ['4', '5', '6', '7', '8', '9', '10', '11', '12'],
    maxPromptLength: 2000,
    needsAspectRatio: true,
    aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1', '21:9'],
    supportsAudio: true,
    defaultAudio: false,
  },
  {
    id: 'i2v_vidu_q3_pro',
    label: 'Vidu Q3 Pro',
    resolutions: ['540p', '720p', '1080p'],
    durations: ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16'],
    maxPromptLength: 2000,
    needsAspectRatio: false,
    supportsAudio: true,
    defaultAudio: true,
  },
  {
    id: 'i2v_wan_2_7',
    label: 'Wan 2.7',
    resolutions: ['720p', '1080p'],
    durations: ['5', '10', '15'],
    maxPromptLength: 5000,
    needsAspectRatio: false,
    supportsAudio: false,
    defaultAudio: false,
  },
  {
    id: 'i2v_wan_2_6',
    label: 'Wan 2.6',
    resolutions: ['720p', '1080p'],
    durations: ['5', '10', '15'],
    maxPromptLength: 800,
    needsAspectRatio: false,
    supportsAudio: false,
    defaultAudio: false,
  },
  {
    id: 'i2v_wan_2_5',
    label: 'Wan 2.5',
    resolutions: ['480p', '720p', '1080p'],
    durations: ['5', '10'],
    maxPromptLength: 800,
    needsAspectRatio: false,
    supportsAudio: false,
    defaultAudio: false,
  },
];

const PRESET_PROMPTS = [
  'Camera slowly zooms into the scene with smooth motion',
  'Gentle swaying motion like a soft breeze blowing',
  'Cinematic dolly shot moving forward through the scene',
  'Slow pan from left to right revealing details',
  'Particles and sparkles floating around magically',
  'Subtle parallax effect creating depth and dimension',
  'Dreamy bokeh blur effect in the background',
  'Time-lapse clouds moving across the sky',
  'Smooth 360-degree rotation around the subject',
  'Dynamic zoom out revealing the full scene',
  'Gentle camera shake adding realism',
  'Soft focus pull from background to foreground',
  'Elegant crane shot rising upward',
  'Flowing water or fabric movement',
  'Leaves gently falling with natural physics',
  'Smoke or mist slowly drifting through',
  'Light rays beaming through creating atmosphere',
  'Wind blowing through hair or grass',
  'Camera orbiting around the center point',
  'Slow motion dramatic reveal',
];

// ==================== Credit Estimation ====================
function estimateCredits(model: string, resolution: string, duration: string, withAudio: boolean): number {
  const dur = Number(duration);
  const res = resolution;
  switch (model) {
    case 'i2v_wan_2_5': {
      const r = res.replace(/p$/i, '');
      const map: Record<string, number> = {
        '480-5': 40, '480-10': 80,
        '720-5': 80, '720-10': 160,
        '1080-5': 120, '1080-10': 240,
      };
      return map[`${r}-${dur}`] ?? 80;
    }
    case 'i2v_wan_2_6':
    case 'i2v_wan_2_7': {
      const r = res.replace(/p$/i, '');
      const map: Record<string, number> = {
        '720-5': 80, '720-10': 160, '720-15': 240,
        '1080-5': 120, '1080-10': 240, '1080-15': 360,
      };
      return map[`${r}-${dur}`] ?? 80;
    }
    case 'i2v_seedance_1_5_pro': {
      const rates: Record<string, { noAudio: number; withAudio: number }> = {
        '480p': { noAudio: 3, withAudio: 4 },
        '720p': { noAudio: 6, withAudio: 10 },
        '1080p': { noAudio: 12, withAudio: 20 },
      };
      const rate = rates[res] ?? rates['720p'];
      return (withAudio ? rate.withAudio : rate.noAudio) * dur;
    }
    case 'i2v_vidu_q3_pro': {
      const rates: Record<string, number> = { '540p': 5, '720p': 10, '1080p': 12 };
      return (rates[res] ?? 10) * dur;
    }
    case 'i2v_aipose_video_4': {
      const isFhd = res === 'FHD';
      const isLong = dur >= 8;
      if (isFhd) return isLong ? 144 : 72;
      return isLong ? 48 : 24;
    }
    default:
      return 80;
  }
}

// ==================== State ====================
const selectedModel = ref('i2v_aipose_video_4');
const imageUrl = ref('');
const imagePreview = ref('');
const prompt = ref('');
const resolution = ref('HD');
const duration = ref('5');
const aspectRatio = ref('9:16');
const withAudio = ref(false);
const isSubmitting = ref(false);
const isUploading = ref(false);
const errorMsg = ref('');
const dragOver = ref(false);

const currentConfig = computed(() => MODEL_CONFIGS.find(m => m.id === selectedModel.value) ?? MODEL_CONFIGS[0]);
const creditCost = computed(() => estimateCredits(selectedModel.value, resolution.value, duration.value, withAudio.value));

// Reset params when model changes
watch(selectedModel, (newModel) => {
  const config = MODEL_CONFIGS.find(m => m.id === newModel) ?? MODEL_CONFIGS[0];
  resolution.value = config.resolutions[0];
  duration.value = config.durations[0];
  if (config.needsAspectRatio && config.aspectRatios?.length) {
    aspectRatio.value = config.aspectRatios.includes('9:16') ? '9:16' : config.aspectRatios[0];
  }
  withAudio.value = config.defaultAudio;
});

// ==================== Upload ====================
async function handleFile(file: File) {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/image-to-video'));
    return;
  }
  if (!file.type.startsWith('image/')) {
    errorMsg.value = 'Please upload an image file';
    return;
  }
  if (file.size > 20 * 1024 * 1024) {
    errorMsg.value = 'Image too large (max 20MB)';
    return;
  }
  errorMsg.value = '';
  isUploading.value = true;
  try {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const sign = await api.getSignUrl(ext);
    await axios.put(sign.sign_url, file, {
      headers: { 'Content-Type': file.type || 'image/png' },
    });
    const url = (sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`;
    await api.confirmUpload({ material_id: sign.material_id, url, format: ext, size: file.size });
    imageUrl.value = url;
    imagePreview.value = URL.createObjectURL(file);
  } catch (e: any) {
    errorMsg.value = e?.message || 'Upload failed';
  } finally {
    isUploading.value = false;
  }
}

function onFileInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) handleFile(file);
  input.value = '';
}

function onDrop(event: DragEvent) {
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) handleFile(file);
}

function clearImage() {
  imageUrl.value = '';
  imagePreview.value = '';
}

function randomPrompt() {
  prompt.value = PRESET_PROMPTS[Math.floor(Math.random() * PRESET_PROMPTS.length)];
}

// ==================== Submit ====================
async function submitGeneration() {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/image-to-video'));
    return;
  }
  if (isSubmitting.value) return;
  if (!imageUrl.value) { errorMsg.value = 'Please upload an image'; return; }
  if (!prompt.value.trim()) { errorMsg.value = 'Please enter a prompt'; return; }

  errorMsg.value = '';
  isSubmitting.value = true;
  try {
    const payload: Record<string, any> = {
      model: selectedModel.value,
      image_url: imageUrl.value,
      prompt: prompt.value.trim(),
      resolution: resolution.value,
      duration: duration.value,
    };
    if (currentConfig.value.needsAspectRatio) {
      payload.aspect_ratio = aspectRatio.value;
    }
    if (currentConfig.value.supportsAudio) {
      payload.with_audio = withAudio.value;
    }

    const res = await api.createI2V(payload);
    // Add to polling queue
    pollingTasks.value.push({
      orderId: res.order_id,
      taskId: res.task_id,
      waitInterval: res.wait_interval,
    });
    // Refresh video list
    await loadVideos();
    // Refresh credits
    commStore.setUserInfo();
  } catch (e: any) {
    errorMsg.value = e?.response?.data?.message || e?.message || 'Generation failed';
  } finally {
    isSubmitting.value = false;
  }
}

// ==================== Video List ====================
interface VideoItem {
  id: number;
  orderId: string;
  toolId: string;
  taskStatus: string;
  resultUrl: string | null;
  taskCreatedAt: string | null;
  totalCredits: number | null;
  params: any;
  errorMessage: string | null;
}

const videos = ref<VideoItem[]>([]);
const isLoadingVideos = ref(false);

async function loadVideos() {
  if (!authStore.isLogin) return;
  isLoadingVideos.value = true;
  try {
    const res = await api.listI2VVideos({ limit: 24 });
    videos.value = res.videos;
  } catch {/* ignore */} finally {
    isLoadingVideos.value = false;
  }
}

// ==================== Polling ====================
interface PollTask { orderId: string; taskId: string; waitInterval: number }
const pollingTasks = ref<PollTask[]>([]);
let pollTimer: ReturnType<typeof setInterval> | null = null;

function startPolling() {
  if (pollTimer) return;
  pollTimer = setInterval(async () => {
    // Also check processing videos from list
    const processingFromList = videos.value
      .filter(v => v.taskStatus === 'PROCESSING' || v.taskStatus === 'PENDING')
      .map(v => ({ orderId: v.orderId, taskId: '', waitInterval: 8 }));

    // Merge with explicit polling tasks
    const allTasks = [...pollingTasks.value];
    for (const pt of processingFromList) {
      if (!allTasks.some(t => t.orderId === pt.orderId)) {
        allTasks.push(pt);
      }
    }

    if (allTasks.length === 0) return;

    let changed = false;
    for (const task of allTasks) {
      if (!task.taskId) {
        // Need to get taskId from video list
        const v = videos.value.find(v => v.orderId === task.orderId);
        if (!v) continue;
        // Just refresh the list
        changed = true;
        continue;
      }
      try {
        const res = await api.fetchI2VResult(task.orderId, task.taskId);
        if (res.task_status === 1 || res.task_status === -1) {
          // Terminal state - remove from polling
          pollingTasks.value = pollingTasks.value.filter(t => t.orderId !== task.orderId);
          changed = true;
        }
      } catch {/* ignore */}
    }

    if (changed) {
      await loadVideos();
      commStore.setUserInfo();
    }
  }, 8000);
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

onMounted(() => {
  loadVideos();
  startPolling();
});
onUnmounted(stopPolling);

// Restart polling when login state changes
watch(() => authStore.isLogin, (val) => {
  if (val) { loadVideos(); startPolling(); }
  else { stopPolling(); videos.value = []; }
});

// ==================== Delete ====================
const deleteTarget = ref<string | null>(null);
const showDeleteConfirm = ref(false);

function confirmDelete(orderId: string) {
  deleteTarget.value = orderId;
  showDeleteConfirm.value = true;
}

async function doDelete() {
  if (!deleteTarget.value) return;
  try {
    await api.deleteI2VVideo(deleteTarget.value);
    videos.value = videos.value.filter(v => v.orderId !== deleteTarget.value);
  } catch {/* ignore */}
  showDeleteConfirm.value = false;
  deleteTarget.value = null;
}

// ==================== Download ====================
async function downloadVideo(url: string) {
  try {
    const res = await fetch(`/api/download?url=${encodeURIComponent(url)}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `i2v-${Date.now()}.mp4`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {/* ignore */}
}

// ==================== Detail Dialog ====================
const detailVideo = ref<VideoItem | null>(null);
const showDetail = ref(false);

function openDetail(video: VideoItem) {
  detailVideo.value = video;
  showDetail.value = true;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString();
}

function modelLabel(toolId: string): string {
  return MODEL_CONFIGS.find(m => m.id === toolId)?.label ?? toolId;
}

// ==================== Hover play ====================
const hoverVideoId = ref<number | null>(null);
</script>

<template>
  <div class="min-h-screen bg-neutral-950 text-white">
    <NewHeader />

    <main class="pt-24 pb-20 px-4 max-w-7xl mx-auto">
      <div class="text-center mb-10">
        <h1 class="text-3xl md:text-4xl font-bold mb-3">Image to Video</h1>
        <p class="text-white/60 max-w-2xl mx-auto">Transform your images into stunning AI-generated videos with 6 different AI models</p>
      </div>

      <div class="flex flex-col lg:flex-row gap-6">
        <!-- ========== Left: Create Panel ========== -->
        <div class="w-full lg:w-[420px] shrink-0 space-y-5">

          <!-- Image Upload -->
          <div class="bg-neutral-900 rounded-2xl p-5">
            <label class="text-sm font-semibold text-white/80 mb-2 block">Upload Image</label>
            <div v-if="!imageUrl"
              @dragover.prevent="dragOver = true"
              @dragleave="dragOver = false"
              @drop.prevent="onDrop"
              class="relative border-2 border-dashed rounded-xl h-48 flex flex-col items-center justify-center cursor-pointer transition-colors"
              :class="dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-neutral-700 hover:border-neutral-500'"
            >
              <input type="file" accept="image/*" class="absolute inset-0 opacity-0 cursor-pointer" @change="onFileInput" />
              <svg class="w-10 h-10 text-neutral-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 16v-8m0 0l-3 3m3-3l3 3M3 16.5V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0021 18v-1.5" /></svg>
              <p class="text-sm text-neutral-400">Drop image here or click to upload</p>
              <p class="text-xs text-neutral-500 mt-1">JPG, PNG, WEBP (max 20MB)</p>
              <div v-if="isUploading" class="absolute inset-0 bg-neutral-900/80 rounded-xl flex items-center justify-center">
                <div class="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
            <div v-else class="relative rounded-xl overflow-hidden">
              <img :src="imagePreview || imageUrl" class="w-full h-48 object-cover rounded-xl" alt="uploaded" />
              <button @click="clearImage" class="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-1.5 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          <!-- Prompt -->
          <div class="bg-neutral-900 rounded-2xl p-5">
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-semibold text-white/80">Prompt</label>
              <button @click="randomPrompt" class="text-xs text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Random
              </button>
            </div>
            <textarea
              v-model="prompt"
              :maxlength="currentConfig.maxPromptLength"
              rows="3"
              placeholder="Describe the motion you want..."
              class="w-full bg-neutral-800 rounded-xl px-4 py-3 text-sm text-white placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none"
            ></textarea>
            <p class="text-xs text-neutral-500 mt-1 text-right">{{ prompt.length }} / {{ currentConfig.maxPromptLength }}</p>
          </div>

          <!-- Model Selection (Dropdown) -->
          <div class="bg-neutral-900 rounded-2xl p-5">
            <label class="text-sm font-semibold text-white/80 mb-2 block">AI Model</label>
            <select
              v-model="selectedModel"
              class="w-full bg-neutral-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500 appearance-none cursor-pointer"
              style="background-image: url(&quot;data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23999' viewBox='0 0 16 16'%3E%3Cpath d='M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E&quot;); background-repeat: no-repeat; background-position: right 12px center;"
            >
              <option v-for="m in MODEL_CONFIGS" :key="m.id" :value="m.id" class="bg-neutral-800">{{ m.label }}</option>
            </select>
          </div>

          <!-- Parameters -->
          <div class="bg-neutral-900 rounded-2xl p-5 space-y-4">
            <!-- Resolution -->
            <div>
              <label class="text-sm font-semibold text-white/80 mb-2 block">Resolution</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="r in currentConfig.resolutions" :key="r"
                  @click="resolution = r"
                  class="px-3 py-1.5 rounded-lg text-sm transition-all"
                  :class="resolution === r ? 'bg-orange-600 text-white' : 'bg-neutral-800 text-white/60 hover:bg-neutral-700'"
                >{{ r }}</button>
              </div>
            </div>

            <!-- Duration -->
            <div>
              <label class="text-sm font-semibold text-white/80 mb-2 block">Duration (seconds)</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="d in currentConfig.durations" :key="d"
                  @click="duration = d"
                  class="px-3 py-1.5 rounded-lg text-sm transition-all"
                  :class="duration === d ? 'bg-orange-600 text-white' : 'bg-neutral-800 text-white/60 hover:bg-neutral-700'"
                >{{ d }}s</button>
              </div>
            </div>

            <!-- Aspect Ratio (conditional) -->
            <div v-if="currentConfig.needsAspectRatio && currentConfig.aspectRatios">
              <label class="text-sm font-semibold text-white/80 mb-2 block">Aspect Ratio</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="ar in currentConfig.aspectRatios" :key="ar"
                  @click="aspectRatio = ar"
                  class="px-3 py-1.5 rounded-lg text-sm transition-all"
                  :class="aspectRatio === ar ? 'bg-orange-600 text-white' : 'bg-neutral-800 text-white/60 hover:bg-neutral-700'"
                >{{ ar }}</button>
              </div>
            </div>

            <!-- Audio Toggle (conditional) -->
            <div v-if="currentConfig.supportsAudio" class="flex items-center justify-between">
              <label class="text-sm font-semibold text-white/80">Generate Audio</label>
              <button
                @click="withAudio = !withAudio"
                class="relative w-11 h-6 rounded-full transition-colors"
                :class="withAudio ? 'bg-orange-600' : 'bg-neutral-700'"
              >
                <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform" :class="withAudio ? 'translate-x-5' : ''"></span>
              </button>
            </div>
          </div>

          <!-- Submit -->
          <div class="bg-neutral-900 rounded-2xl p-5">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-white/60">Estimated Cost</span>
              <span class="text-orange-400 font-bold">{{ creditCost }} credits</span>
            </div>
            <p v-if="errorMsg" class="text-red-400 text-sm mb-3">{{ errorMsg }}</p>
            <button
              @click="submitGeneration"
              :disabled="isSubmitting || isUploading || !imageUrl || !prompt.trim()"
              class="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200"
              :class="isSubmitting || isUploading || !imageUrl || !prompt.trim()
                ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-orange-600 to-rose-600 text-white hover:from-orange-500 hover:to-rose-500 shadow-lg shadow-orange-600/20'"
            >
              <span v-if="isSubmitting" class="flex items-center justify-center gap-2">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </span>
              <span v-else>Generate Video</span>
            </button>
          </div>
        </div>

        <!-- ========== Right: Results ========== -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold">Your Videos</h2>
            <button @click="loadVideos" class="text-xs text-neutral-400 hover:text-white transition-colors">
              Refresh
            </button>
          </div>

          <!-- Login CTA -->
          <div v-if="!authStore.isLogin" class="bg-neutral-900 rounded-2xl p-10 text-center">
            <p class="text-white/60 mb-4">Log in to generate and view your videos</p>
            <NuxtLink :to="localePath('/login/google?redirect=/image-to-video')" class="inline-flex items-center px-6 py-2.5 bg-orange-600 rounded-full text-sm font-semibold hover:bg-orange-500 transition-colors">
              Log In
            </NuxtLink>
          </div>

          <!-- Empty State -->
          <div v-else-if="!isLoadingVideos && videos.length === 0" class="bg-neutral-900 rounded-2xl p-10 text-center">
            <svg class="w-12 h-12 mx-auto text-neutral-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            <p class="text-white/40">No videos yet. Generate your first one!</p>
          </div>

          <!-- Video Grid -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <div
              v-for="video in videos" :key="video.id"
              class="bg-neutral-900 rounded-xl overflow-hidden group relative cursor-pointer"
              @mouseenter="hoverVideoId = video.id"
              @mouseleave="hoverVideoId = null"
              @click="openDetail(video)"
            >
              <!-- Video/Thumbnail -->
              <div class="aspect-video bg-neutral-800 relative">
                <!-- Completed: video with hover play -->
                <template v-if="video.taskStatus === 'COMPLETED' && video.resultUrl">
                  <video
                    v-if="hoverVideoId === video.id"
                    :src="video.resultUrl"
                    class="w-full h-full object-cover"
                    autoplay muted loop playsinline
                  ></video>
                  <video
                    v-else
                    :src="video.resultUrl"
                    class="w-full h-full object-cover"
                    preload="metadata"
                  ></video>
                  <!-- Play icon overlay -->
                  <div v-if="hoverVideoId !== video.id" class="absolute inset-0 flex items-center justify-center">
                    <div class="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center">
                      <svg class="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  </div>
                </template>

                <!-- Processing -->
                <template v-else-if="video.taskStatus === 'PROCESSING' || video.taskStatus === 'PENDING'">
                  <div class="absolute inset-0 flex flex-col items-center justify-center">
                    <div class="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span class="text-xs text-white/50">Processing...</span>
                  </div>
                </template>

                <!-- Failed -->
                <template v-else>
                  <div class="absolute inset-0 flex flex-col items-center justify-center">
                    <svg class="w-8 h-8 text-red-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                    <span class="text-xs text-red-400">Failed</span>
                  </div>
                </template>
              </div>

              <!-- Info bar -->
              <div class="px-3 py-2 flex items-center justify-between">
                <div class="min-w-0">
                  <p class="text-xs text-white/50 truncate">{{ modelLabel(video.toolId) }}</p>
                  <p class="text-[10px] text-white/30">{{ formatDate(video.taskCreatedAt) }}</p>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <!-- Download button -->
                  <button
                    v-if="video.taskStatus === 'COMPLETED' && video.resultUrl"
                    @click.stop="downloadVideo(video.resultUrl!)"
                    class="p-1.5 rounded-lg hover:bg-neutral-700 transition-colors"
                    title="Download"
                  >
                    <svg class="w-4 h-4 text-white/50 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  </button>
                  <!-- Delete button (only for terminal states) -->
                  <button
                    v-if="video.taskStatus === 'COMPLETED' || video.taskStatus === 'FAILED'"
                    @click.stop="confirmDelete(video.orderId)"
                    class="p-1.5 rounded-lg hover:bg-neutral-700 transition-colors"
                    title="Delete"
                  >
                    <svg class="w-4 h-4 text-white/50 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Delete Confirmation Dialog -->
    <Teleport to="body">
      <div v-if="showDeleteConfirm" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" @click.self="showDeleteConfirm = false">
        <div class="bg-neutral-900 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
          <h3 class="text-lg font-semibold mb-2">Delete Video</h3>
          <p class="text-sm text-white/60 mb-5">Are you sure you want to delete this video? This action cannot be undone.</p>
          <div class="flex gap-3 justify-end">
            <button @click="showDeleteConfirm = false" class="px-4 py-2 rounded-lg text-sm bg-neutral-800 hover:bg-neutral-700 transition-colors">Cancel</button>
            <button @click="doDelete" class="px-4 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-500 text-white transition-colors">Delete</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Detail Dialog -->
    <Teleport to="body">
      <div v-if="showDetail && detailVideo" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" @click.self="showDetail = false">
        <div class="bg-neutral-900 rounded-2xl max-w-2xl w-full mx-4 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
          <!-- Video -->
          <div class="aspect-video bg-black" v-if="detailVideo.taskStatus === 'COMPLETED' && detailVideo.resultUrl">
            <video :src="detailVideo.resultUrl" class="w-full h-full object-contain" controls autoplay></video>
          </div>
          <div v-else class="aspect-video bg-neutral-800 flex items-center justify-center">
            <span class="text-white/40">{{ detailVideo.taskStatus === 'FAILED' ? 'Generation failed' : 'Processing...' }}</span>
          </div>
          <!-- Info -->
          <div class="p-5 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm font-semibold">{{ modelLabel(detailVideo.toolId) }}</span>
              <span class="text-xs px-2 py-0.5 rounded-full" :class="{
                'bg-green-600/20 text-green-400': detailVideo.taskStatus === 'COMPLETED',
                'bg-yellow-600/20 text-yellow-400': detailVideo.taskStatus === 'PROCESSING' || detailVideo.taskStatus === 'PENDING',
                'bg-red-600/20 text-red-400': detailVideo.taskStatus === 'FAILED',
              }">{{ detailVideo.taskStatus }}</span>
            </div>
            <div v-if="detailVideo.params?.prompt" class="text-sm text-white/60">
              <p class="text-xs text-white/40 mb-1">Prompt</p>
              {{ detailVideo.params.prompt }}
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40">
              <span v-if="detailVideo.params?.resolution">Resolution: {{ detailVideo.params.resolution }}</span>
              <span v-if="detailVideo.params?.duration">Duration: {{ detailVideo.params.duration }}s</span>
              <span v-if="detailVideo.params?.aspectRatio">Aspect: {{ detailVideo.params.aspectRatio }}</span>
              <span v-if="detailVideo.totalCredits">Cost: {{ detailVideo.totalCredits }} credits</span>
              <span v-if="detailVideo.taskCreatedAt">Created: {{ formatDate(detailVideo.taskCreatedAt) }}</span>
            </div>
            <div v-if="detailVideo.errorMessage" class="text-sm text-red-400">
              Error: {{ detailVideo.errorMessage }}
            </div>
            <div class="flex gap-3 pt-2">
              <button
                v-if="detailVideo.taskStatus === 'COMPLETED' && detailVideo.resultUrl"
                @click="downloadVideo(detailVideo.resultUrl!)"
                class="flex-1 py-2.5 rounded-xl text-sm font-medium bg-orange-600 hover:bg-orange-500 transition-colors text-center"
              >Download</button>
              <button @click="showDetail = false" class="flex-1 py-2.5 rounded-xl text-sm font-medium bg-neutral-800 hover:bg-neutral-700 transition-colors">Close</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <NewFooter />
  </div>
</template>
